import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';

import {
  glacierPolicyApiDeletePolicyRenewalFlagMutation,
  glacierPolicyApiGetPolicyQueryKey,
  glacierPolicyApiListUnderwriterNotesOptions,
} from '@/lib/heyapi/@tanstack/react-query.gen';
import { RenewalFlagEnum } from '@/lib/heyapi/types.gen';

import type { Message } from '../../StandardMessage';
import { REMOVED_MANUAL_REVIEW_FLAG_NOTE } from './helpers';

export const RemoveManualReviewFlag = ({
  accountId,
  isOpen,
  onOpenChange,
  setMessage,
}: {
  accountId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  setMessage: (message: Message | undefined) => void;
}) => {
  const [note, setNote] = useState('');
  const queryClient = useQueryClient();

  const { queryKey } = glacierPolicyApiListUnderwriterNotesOptions({
    path: {
      account_id: accountId,
    },
  });

  const policyQK = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: accountId,
    },
  });

  const { mutate: removeManualReviewFlag, isPending } = useMutation({
    ...glacierPolicyApiDeletePolicyRenewalFlagMutation(),
    onMutate: () => {
      setMessage({ content: 'Removing manual review flag...', level: 'INFO' });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: policyQK });
      queryClient.invalidateQueries({ queryKey });
      setNote('');
      onOpenChange(false);
      toast({
        title: 'Policy Manual Review Flag Removed',
        description: <p>Policy manual review flag removed successfully</p>,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: (
          <p>
            {(error as { detail?: string })?.detail ||
              'An error occurred while removing the manual review flag'}
          </p>
        ),
      });
    },
    onSettled: () => {
      setMessage(undefined);
    },
  });

  const handleRemoveFlag = () => {
    removeManualReviewFlag({
      query: {
        note: `${REMOVED_MANUAL_REVIEW_FLAG_NOTE} ${note.trim()}`,
      },
      path: {
        account_id: accountId,
        flag: RenewalFlagEnum.FLAG_FOR_MANUAL_REVIEW,
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove Manual Review Flag</DialogTitle>
          <DialogDescription>
            Removing the manual review flag will allow the system to
            automatically create a renewal for this account. Do you wish to
            continue?
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="remove-manual-review-note">Reason for Removal</Label>
          <Textarea
            id="remove-manual-review-note"
            placeholder="Reason for removing manual review flag..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={isPending}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            disabled={isPending || !note.trim()}
            loading={isPending}
            onClick={handleRemoveFlag}
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
